import {
  Box,
  Button,
  Container,
  FormControl,
  FormLabel,
  Grid,
  InputAdornment,
  Link,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import { UserType, IUser } from 'types'
import { inject, observer } from 'mobx-react'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { Pages } from 'routes'
import { AuthStore, RoutingStore } from 'stores'
import { FixedLogo, Icon, Page } from 'components'


export interface IAdminLoginProps {
  authStore?:    AuthStore,
  routingStore?: RoutingStore,
}

export const AdminLogin = inject('authStore', 'routingStore')(observer((props: IAdminLoginProps) => {
  const { t } = useTranslation();
  const { authStore, routingStore } = props;

  const [showPassword, setShowPassword] = React.useState(false);
  const [userType,     setUserType]     = React.useState<UserType>(null);
  const [email,        setEmail]        = React.useState<string>('');
  const [password,     setPassword]     = React.useState<string>('');

  const devemail    = '@bgi.com';
  const devpassword = 'some';

  const users: Partial<IUser>[] = [
    UserType.Participant,
    UserType.Admin,
    UserType.Manager,
    UserType.Analyst,
    UserType.Editor,
  ].map((t) => ({
    user_type: t,
    email:     t + devemail,
    password:  devpassword,
  }));

  React.useEffect(() => {
    const queryString    = window.location.search;
    const urlParams      = new URLSearchParams(queryString);
    const grandidsession = urlParams.get('grandidsession');
    if (grandidsession) {
      authStore.validateBankIdLoginCallback(grandidsession);
      const url = new URL(window.location.href);
      url.searchParams.delete('grandidsession');
      window.history.replaceState({}, '', url);
    }
  }, []);

  React.useEffect(() => {
    const u = users.find((x) => x.user_type === userType);
    if (u) {
      setEmail(u.email);
      setPassword(u.password);
    } else {
      setEmail('');
      setPassword('');
    }
  }, [userType]);

  React.useEffect(() => {
    if (authStore.getCurUserType) {
      // TODO Check history if this was a re-route and route accordingly
      routingStore.push(RoutingStore.startPage[authStore.getCurUserType]);
    }
  }, [props.authStore.getCurUser]);

  return (
    <Page isLoading={authStore.isAuthenticating}>
      <Container maxWidth='sm'>
        <FixedLogo />
        <Grid
          container
          direction      = 'column'
          alignItems     = 'center'
          justifyContent = 'center'
          width          = '100%'
          sx             = {{ minHeight: '100vh' }}
        >
          <Grid item xs={12}>
            <Typography variant='h2' textAlign='center'>
              {t('login_to_your_account')}
            </Typography>
              <Box width='100%' mt={4}>
                <FormControl fullWidth margin='normal'>
                  <FormLabel>{t('email')}</FormLabel>
                  <TextField
                    hiddenLabel
                    type         = 'text'
                    autoComplete = 'new-password'
                    value        = {email}
                    variant      = 'outlined'
                    onChange     = {(e) => setEmail(e.target.value)}
                  />
                </FormControl>
                <FormControl fullWidth margin='normal'>
                  <FormLabel>{t('password')}</FormLabel>
                  <TextField
                    hiddenLabel
                    type         = {showPassword ? 'text' : 'password'}
                    autoComplete = 'new-password'
                    value        = {password}
                    variant      = 'outlined'
                    InputProps   = {{
                      endAdornment: (
                        <InputAdornment
                          sx       = {{ cursor: 'pointer' }}
                          position = 'end'
                        >
                          <Icon
                            size    = 'size-18'
                            onClick = {() => setShowPassword(!showPassword)}
                            iconId  = {showPassword ? 'view' : 'view-off'}
                          />
                        </InputAdornment>
                      ),
                    }}
                    onChange   = {(e) => setPassword(e.target.value)}
                    onKeyPress = {(e) => {
                      if (e.key === 'Enter') {
                        props.authStore.login(email, password)
                        e.preventDefault()
                      }
                    }}
                  />
                </FormControl>
                <Box
                  display       = 'flex'
                  flexDirection = 'column'
                  alignItems    = 'center'
                  gap           = {3}
                  mt            = {3}
                >
                  <Button
                    onClick  = {() => props.authStore.login(email, password)}
                    variant  = 'contained'
                    disabled = {props.authStore.isLoggedIn || !email || !password}
                  >
                    {t('login')}
                  </Button>
                  <Link
                    color   = 'secondary'
                    onClick = {() => routingStore.push(Pages.ForgotPassword)}
                  >
                    {t('forgot_password')}
                  </Link>
                </Box>
              </Box>
            <Box>
              <Typography textAlign='center' color='red'>
                {t(props.authStore.error_message)}
              </Typography>
            </Box>

            {(process.env.NODE_ENV != 'production') && (
              <Box width='100%'>
                <Typography variant='body2' mt={2} textAlign='center'>
                  {t('login_or_select_select_user_type')}
                </Typography>
                <FormControl fullWidth margin='normal'>
                  <Select
                    displayEmpty
                    labelId    = 'demo-simple-select-label'
                    id         = 'demo-simple-select'
                    value      = {userType || ''}
                    onChange   = {(event) => setUserType(event.target.value as UserType)}
                    inputProps = {{ 'aria-label': 'Select' }}
                  >
                    <MenuItem value='' disabled>
                      {t('select_user_type')}
                    </MenuItem>
                    {users.map((u, i) => (
                      <MenuItem key={u.user_type} value={u.user_type}>
                        {u.email}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}
          </Grid>
        </Grid>
      </Container>
    </Page>
  );
}));
